/**
 * Per-tank sprite configuration (client-only, PIXI-adjacent). Keyed by tank
 * type id (see core/tankTypes.ts). Kept separate from core so the headless
 * runtime never imports rendering code.
 *
 * A tank declares its animations per state. The worm uses 8 vertical-strip
 * sheets (pixel art, NEAREST). The wizard uses a single 6x6 GRID idle sheet
 * (smooth art, LINEAR) and falls back to idle for every state for now - the
 * structure already supports adding walk/jump/die sheets later.
 *
 * All tanks declare a target on-screen `displayHeight`; the sprite scales each
 * tank's native frames to it so every tank renders at the same in-play size.
 */

export type TankAnimState =
  | 'idle'
  | 'walk'
  | 'jump'
  | 'fall'
  | 'backflip'
  | 'die'
  | 'parachute'
  | 'kamikaze'
  // GunBound mobiles: shown while the player is charging/dragging the shot.
  | 'charge';

export interface TankAnimSource {
  path: string;
  fps: number;
  loop: boolean;
  /** Vertical-strip sheet: each frame is full-width x frameHeight, stacked. */
  frameHeight?: number;
  /** Grid sheet: cols x rows (row-major), optional explicit frameCount. */
  grid?: { cols: number; rows: number; frameCount?: number };
  /** LINEAR (smooth art) vs NEAREST (pixel art). Default: strip=pixel,
   *  grid=smooth. */
  smooth?: boolean;
  /** Per-animation overrides of the config-level sizing, for sheets whose
   *  frame dimensions differ between animations (e.g. the wizard's idle frame
   *  is taller to fit rising smoke). Each falls back to the config value. */
  nativeFrameHeight?: number;
  displayHeight?: number;
  footOffsetPx?: number;
}

export interface TankSpriteConfig {
  id: string;
  /** Target rendered height in world px - normalizes all tanks to one size. */
  displayHeight: number;
  /** Native source frame height (px); base scale = displayHeight / native. */
  nativeFrameHeight: number;
  /** Animations by state; missing states render the `fallback` animation. */
  animations: Partial<Record<TankAnimState, TankAnimSource>>;
  fallback: TankAnimState;
  /** Keep animating even when the tank is standing still (GunBound mobiles
   *  idle-bob continuously). Default false: worms freeze on a frame when idle. */
  idleAnimatesWhenStill?: boolean;
  /** Set when the source art faces RIGHT by default (the worm faces LEFT).
   *  Inverts the facing flip so the tank looks toward its aim. */
  mirrorFacing?: boolean;
  /** World px to drop the sprite's bottom edge below the worm's collision feet,
   *  to sit the drawn feet on the ground. = the empty gap below the feet in the
   *  source frame. Worm art has ~17px of empty space below its feet (18);
   *  tanks whose feet reach the frame bottom (wizard wheels) use ~0. Default 18. */
  footOffsetPx?: number;
}

/** Shared in-play tank height (px). Worm art is natively 60px, so the worm
 *  stays pixel-identical; other tanks scale to match. */
export const TANK_DISPLAY_HEIGHT = 60;

const WORM_FRAME = 60;
const strip = (path: string, fps: number, loop: boolean): TankAnimSource => ({
  path,
  frameHeight: WORM_FRAME,
  fps,
  loop,
  smooth: false,
});

export const WORM_SPRITE_CONFIG: TankSpriteConfig = {
  id: 'worm',
  displayHeight: TANK_DISPLAY_HEIGHT,
  nativeFrameHeight: WORM_FRAME,
  fallback: 'idle',
  footOffsetPx: 18,
  animations: {
    idle: strip('/sprites/Worms/wbrth1.png', 10, true),
    walk: strip('/sprites/Worms/wwalk.png', 15, true),
    jump: strip('/sprites/Worms/wjump.png', 20, false),
    fall: strip('/sprites/Worms/wfall.png', 8, true),
    backflip: strip('/sprites/Worms/wbackflp.png', 25, false),
    die: strip('/sprites/Worms/wdie.png', 20, false),
    parachute: strip('/sprites/Worms/wparacht.png', 10, true),
    kamikaze: strip('/sprites/Worms/wkami3.png', 15, true),
  },
};

export const WIZARD_SPRITE_CONFIG: TankSpriteConfig = {
  id: 'wizard',
  // Config-level sizing = the IDLE sheet (the fallback). Idle frames are
  // 100x159 (taller, to fit the rising smoke above the cauldron); displayHeight
  // 75 keeps the cauldron itself ~the same on-screen size as the worm/charge
  // (baseScale ~0.47), with the smoke extending above. footOffset 8 sits the
  // wheels (16px above the idle frame bottom) on the ground.
  displayHeight: 75,
  nativeFrameHeight: 159,
  footOffsetPx: 8,
  fallback: 'idle',
  // The wizard bobs/casts continuously.
  idleAnimatesWhenStill: true,
  // Source art faces right (opposite the worm), so invert the facing flip.
  mirrorFacing: true,
  animations: {
    // Calm idle: wizard in the cauldron with rising smoke (36-frame loop).
    idle: {
      path: '/sprites/Tanks/wizard_idle.png',
      grid: { cols: 6, rows: 6, frameCount: 36 },
      fps: 12,
      loop: true,
      smooth: true,
    },
    // Charging/casting: shown while the player drags/charges the shot. Its
    // sheet is a tighter 96x128 frame, so it overrides the sizing to keep the
    // cauldron the same size + grounded as idle.
    charge: {
      path: '/sprites/Tanks/wizard_charge.png',
      grid: { cols: 6, rows: 6, frameCount: 36 },
      fps: 16,
      loop: true,
      smooth: true,
      nativeFrameHeight: 128,
      displayHeight: 60,
      footOffsetPx: 2,
    },
    // Walking: the cauldron rolls (animated wheels) while the worm moves. Same
    // tight ~91x128 framing as charge, so it shares the sizing overrides.
    walk: {
      path: '/sprites/Tanks/wizard_walk.png',
      grid: { cols: 6, rows: 6, frameCount: 36 },
      // Brisk roll: 36 frames at 30fps -> ~1.2s loop (14fps felt sluggish).
      fps: 30,
      loop: true,
      smooth: true,
      nativeFrameHeight: 128,
      displayHeight: 60,
      footOffsetPx: 2,
    },
  },
};

/** Shared on-screen height for the GunBound mobiles (worm-comparable). */
const MOBILE_DISPLAY_HEIGHT = 50;

/** One animation of a mobile: a 20-frame horizontal strip (1 row) extracted
 *  from the gunbunda mobile art, tight-cropped so the feet sit at the frame
 *  bottom. Pixel art -> NEAREST. */
const mobileStrip = (path: string, fps: number, frames = 20): TankAnimSource => ({
  path,
  grid: { cols: frames, rows: 1, frameCount: frames },
  fps,
  loop: true,
  smooth: false,
});

/** A GunBound mobile sprite. `nativeFrameHeight` is the tight-cropped frame
 *  height (px); feet are at the frame bottom so footOffset is 0. `walkPath` is
 *  optional - when a mobile has no good walk animation (e.g. aduka's "move"
 *  range is a crouch that reads as dying) walk falls back to idle. `idleFrames`
 *  defaults to the 20-frame strips; a static sprite passes 1 (no idle bob).
 *  Paths are literals so the asset-shake build keeps them. */
const mobileConfig = (
  id: string,
  nativeFrameHeight: number,
  idlePath: string,
  walkPath?: string,
  idleFrames = 20,
): TankSpriteConfig => ({
  id,
  displayHeight: MOBILE_DISPLAY_HEIGHT,
  nativeFrameHeight,
  footOffsetPx: 0,
  fallback: 'idle',
  idleAnimatesWhenStill: idleFrames > 1,
  animations: {
    idle: mobileStrip(idlePath, 10, idleFrames),
    ...(walkPath ? { walk: mobileStrip(walkPath, 14) } : {}),
  },
});

const TANK_SPRITE_CONFIGS: Record<string, TankSpriteConfig> = {
  worm: WORM_SPRITE_CONFIG,
  wizard: WIZARD_SPRITE_CONFIG,
  // GunBound mobiles (art extracted from ../gunbunda). Tight-cropped frame
  // heights: grub 46, aduka 41, nak 37, lightning 39.
  grub: mobileConfig('grub', 46, '/sprites/Tanks/grub_idle.png', '/sprites/Tanks/grub_walk.png'),
  // aduka's gunbound "move" range (20-39) is a crouch that looks like dying,
  // so it has no walk sheet - it bobs in place (idle) while moving.
  aduka: mobileConfig('aduka', 41, '/sprites/Tanks/aduka_idle.png'),
  nak: mobileConfig('nak', 37, '/sprites/Tanks/nak_idle.png', '/sprites/Tanks/nak_walk.png'),
  lightning: mobileConfig(
    'lightning',
    39,
    '/sprites/Tanks/lightning_idle.png',
    '/sprites/Tanks/lightning_walk.png',
  ),
  // Frog (J.Frog) art is a packed CSS-sprite atlas (Frog2D.png), not the
  // 80-frame strip - its row 0 is a continuous sequence with no clean loop
  // point, so it uses a single static frame (no walk, no idle bob).
  frog: mobileConfig('frog', 40, '/sprites/Tanks/frog_idle.png', undefined, 1),
  jd: mobileConfig('jd', 47, '/sprites/Tanks/jd_idle.png', '/sprites/Tanks/jd_walk.png'),
};

export function getTankSpriteConfig(tankTypeId?: string | null): TankSpriteConfig {
  return (tankTypeId && TANK_SPRITE_CONFIGS[tankTypeId]) || WORM_SPRITE_CONFIG;
}
